import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../ui/tooltip';
import {
  Search,
  Plus,
  MessageCircle,
  MoreVertical,
  X,
  Download,
  Trash2,
  Pencil,
} from 'lucide-react';

const ChatSidebar = ({
  sessionsList = [],
  currentSessionId,
  onSessionSwitch,
  onNewConversation,
  onClearSession,
  width = 320,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Filter sessions based on search query
  const filteredSessions = useMemo(() => {
    // Ensure sessionsList is always an array
    const sessions = Array.isArray(sessionsList) ? sessionsList : [];

    if (!searchQuery.trim()) return sessions;

    return sessions.filter(session => {
      const title = session.title || '新对话';
      const lastMessage = session.lastMessage || '';
      return (
        title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  }, [sessionsList, searchQuery]);

  // Get session display info
  const getSessionDisplayInfo = (session) => {
    const title = session.title || '新对话';
    const timestamp = session.updatedAt || session.createdAt;
    const messageCount = session.messageCount || 0;
    const lastMessage = session.lastMessage || '暂无消息';

    // Format timestamp
    const formatTime = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (diffDays === 1) {
        return '昨天';
      } else if (diffDays < 7) {
        return `${diffDays} 天前`;
      } else {
        return date.toLocaleDateString();
      }
    };

    return {
      title,
      lastMessage: lastMessage.length > 50 ? lastMessage.substring(0, 50) + '...' : lastMessage,
      timestamp: formatTime(timestamp),
      messageCount,
    };
  };

  // Handle session actions
  const handleSessionAction = (action, session) => {
    switch (action) {
      case 'clear':
        setConfirmDialog({
          type: 'clear',
          session,
          title: '清除会话',
          message: '这将删除此会话中的所有消息。此操作不可撤销。',
          onConfirm: () => {
            if (session?.id === currentSessionId) {
              onClearSession();
            }
            setConfirmDialog(null);
          },
        });
        break;
      case 'export':
        // TODO: Implement session export
        console.log('Export session:', session);
        break;
      case 'delete':
        setConfirmDialog({
          type: 'delete',
          session,
          title: '删除会话',
          message: '这将永久删除此会话及其所有消息。此操作不可撤销。',
          onConfirm: () => {
            // TODO: Implement session deletion
            console.log('Delete session:', session);
            setConfirmDialog(null);
          },
        });
        break;
      case 'rename':
        // TODO: Implement session rename
        console.log('Rename session:', session);
        break;
      default:
        break;
    }
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog(null);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  return (
    <div
      className="h-full flex flex-col bg-card"
      style={{ width }}
    >
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            聊天会话
          </h2>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  onClick={onNewConversation}
                  className="h-8 w-8"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>新对话</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="搜索对话..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-8 bg-muted"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
              onClick={handleClearSearch}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-auto">
        {filteredSessions.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground">
            <MessageCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">
              {searchQuery ? '没有匹配的对话' : '暂无对话'}
            </p>
            {!searchQuery && (
              <Button
                variant="link"
                onClick={onNewConversation}
                className="mt-2"
              >
                开始您的第一次对话
              </Button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredSessions.map((session) => {
              const { title, lastMessage, timestamp, messageCount } = getSessionDisplayInfo(session);
              const isActive = session.id === currentSessionId;

              return (
                <div
                  key={session.id}
                  className={`flex items-start p-3 cursor-pointer transition-colors ${
                    isActive
                      ? 'bg-primary/10 hover:bg-primary/10'
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => onSessionSwitch(session.id)}
                >
                  <div className="mr-3 mt-1">
                    <MessageCircle
                      className={`h-5 w-5 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-sm truncate ${
                          isActive ? 'font-semibold' : 'font-normal'
                        }`}
                      >
                        {title}
                      </span>
                      {messageCount > 0 && (
                        <Badge variant="secondary" className="h-4 text-[10px]">
                          {messageCount}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {lastMessage}
                    </p>
                    <span className="text-[10px] text-muted-foreground">
                      {timestamp}
                    </span>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 ml-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleSessionAction('rename', session)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        重命名
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleSessionAction('export', session)}>
                        <Download className="h-4 w-4 mr-2" />
                        导出
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleSessionAction('clear', session)}>
                        <X className="h-4 w-4 mr-2" />
                        清除消息
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleSessionAction('delete', session)}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        删除会话
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={Boolean(confirmDialog)} onOpenChange={handleCloseConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirmDialog?.title}</DialogTitle>
            <DialogDescription>{confirmDialog?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseConfirmDialog}>取消</Button>
            <Button
              onClick={confirmDialog?.onConfirm}
              variant={confirmDialog?.type === 'delete' ? 'destructive' : 'default'}
            >
              {confirmDialog?.type === 'delete' ? '删除' : '确认'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatSidebar;
